import { Fragment, startTransition, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { PuffLoader } from "react-spinners";
import { Checkbox, CustomButton, Label, Tooltip as UITooltip } from "@helloaudio/ui";
import { forEach, isNil, sortBy } from "lodash";

import { AiContentData1, AiContentData2 } from "../../../../constants/AiContentData";
import AIContentModal from "../Modal/AIContentModal";
import apiCall, { makeUrl } from "../../../../utils/apiCall";
import { setPromiseTimeout } from "../../../../utils/timers";
import AIContentGeneratedRow from "./AIContentGeneratedRow";

const COMBINED_CONTENT_LABELS = AiContentData1.concat(AiContentData2);

AIContent.propTypes = {
  aiContentEnabled: PropTypes.oneOf([0, 1]).isRequired,
  feedEpisodeId: PropTypes.string.isRequired,
  feedEpisode: PropTypes.object.isRequired,
};

function Loading({ size = 150 }) {
  return (
    <div className="tw-flex tw-items-center tw-justify-center tw-mb-4 tw-mt-8 tw-w-full">
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
        <PuffLoader color="#32325d" size={size} aria-label="Loading Spinner" data-testid="loader" />
        <div className="tw-inline-block tw-mt-2 tw-text-sm tw-font-Montserrat">Loading</div>
      </div>
    </div>
  );
}

Loading.propTypes = {
  size: PropTypes.number,
};

function AIContent({ aiContentEnabled, feedEpisodeId, feedEpisode }) {
  const navigate = useNavigate();
  const [isShowModal, setIsShowModal] = useState(false);
  const [aiContentData, setAiContentData] = useState({});
  const [aiContentOriginal, setAiContentOriginal] = useState({});
  const [aiContentGenerated, setAiContentGenerated] = useState([]);
  const [isShowRegenerateModal, setIsShowRegenerateModal] = useState(false);
  const [episodeMinutes, setEpisodeMinutes] = useState(null);
  const [minutesBalance, setMinutesBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const length = isNil(feedEpisode.length)
      ? null
      : Math.floor(feedEpisode.length / 60) < 1
        ? 1
        : Math.floor(feedEpisode.length / 60);
    setEpisodeMinutes(length);
  }, [feedEpisode]);

  function filterContentByType(contentRows) {
    const seen = {};
    contentRows.sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
    contentRows = contentRows.filter((item) => {
      return seen[item.contentType] ? false : (seen[item.contentType] = true);
    });
    return contentRows;
  }

  const loadAIContent = async () => {
    const getAIContentApiCall = await apiCall();
    const {
      data: { aiContent },
    } = await getAIContentApiCall.get(makeUrl("/ai-content?feedEpisodeId=" + feedEpisodeId));
    if (aiContent) {
      setAiContentData(aiContent);
      setAiContentOriginal(aiContent);
    }
    setIsLoading(false);
  };

  const loadMinutesBalance = async () => {
    const getMinutesBalanceApiCall = await apiCall();
    const {
      data: { minutes },
    } = await getMinutesBalanceApiCall.get(makeUrl("/ai-content-minutes"));
    setMinutesBalance(minutes);
  };

  const loadAIContentGenerated = async () => {
    const getAIContentApiCall = await apiCall();
    const { data } = await getAIContentApiCall.get(
      makeUrl("/ai-generated-content?feedEpisodeId=" + feedEpisodeId),
    );

    const filteredContent = filterContentByType(data.aiContentGenerated);
    const inProgress = filteredContent.find(
      (a) => a.contentStatus === "ordered" || a.contentStatus === "processing",
    );

    if (inProgress) {
      setTimeout(() => {
        console.log("checking for new content...");
        loadAIContentGenerated();
      }, 5000);
    }
    setAiContentGenerated(filteredContent);
  };

  useEffect(() => {
    if (aiContentEnabled) {
      loadAIContent();
      loadMinutesBalance();
      loadAIContentGenerated();
    } else {
      setTimeout(() => setIsLoading(false), 300);
    }
  }, []);

  const onClickAiContent = (item) => {
    const aiContentToSet = { ...aiContentData };
    const field = item.field;
    const enablingField = !aiContentData[field];

    // Always enable the transcript when the summary is enabled
    if (enablingField || !["aiTranscript", "aiSummary"].includes(field)) {
      aiContentToSet[field] = enablingField;
      aiContentToSet.aiTranscript = true;
      aiContentToSet.aiSummary = true;
    }

    // If no other content is enabled and we are disabling
    // transcript or summary, then disable both of them
    if (!enablingField && ["aiTranscript", "aiSummary"].includes(field)) {
      let otherContentEnabled = false;
      const filteredLabels = COMBINED_CONTENT_LABELS.filter(
        (item) => !["aiTranscript", "aiSummary"].includes(item.field),
      );
      forEach(filteredLabels, (item) => {
        if (item.field !== field && aiContentToSet[item.field]) {
          otherContentEnabled = true;
          return false;
        }
      });
      if (!otherContentEnabled) {
        aiContentToSet.aiTranscript = false;
        aiContentToSet.aiSummary = false;
      }
    }

    setAiContentData(aiContentToSet);
  };

  const submitAIContent = async () => {
    const toSave = {
      ...aiContentOriginal,
      ...aiContentData,
    };
    const submitAIContentApiCall = await apiCall();
    const { data: aiContentId } = await submitAIContentApiCall.post(makeUrl("/ai-content"), {
      feedEpisodeId,
      ...aiContentOriginal,
      ...aiContentData,
    });
    setAiContentOriginal({ ...toSave, ...aiContentId });
    setIsShowModal(false);
    await setPromiseTimeout(1000);
    loadAIContentGenerated();
  };

  const confirmOrRegenerateContent = (data) => {
    const contentType = data.contentType;
    if (contentType !== "aiTranscript") {
      return regenerateContent(data);
    }
    setIsShowRegenerateModal(true);
  };

  const onClickSubscription = (ev) => {
    ev.preventDefault();
    startTransition(() => {
      navigate("/subscription");
    });
  };

  const onClickAddMinutes = () => {
    startTransition(() => {
      navigate("/subscription?add_ai_minutes=1");
    });
  };

  const regenerateContent = async (data) => {
    let newAIContentGenerated = aiContentGenerated.filter(
      (item) => item.aiContentGeneratedId !== data.aiContentGeneratedId,
    );
    newAIContentGenerated.push({ ...data, contentStatus: "ordered" });
    setAiContentGenerated(newAIContentGenerated);

    // Construct the api call to regenerate the content
    // The endpoint is different for summary and transcript generation
    const payload = {
      aiContentId: data.aiContentId,
      contentType: data.contentType,
    };
    let regenerateEndpoint = "/ai-generated-content";
    if (data.contentType === "aiSummary") {
      regenerateEndpoint = "/ai-content/summary";
      delete payload.contentType;
    } else if (data.contentType === "aiTranscript") {
      regenerateEndpoint = "/ai-content/transcript";
      delete payload.contentType;
    }

    const regenerateContentApiCall = await apiCall();
    await regenerateContentApiCall.post(makeUrl(regenerateEndpoint), payload);
    await setPromiseTimeout(1000);
    await loadAIContentGenerated();
    loadMinutesBalance();
  };

  // Sorting the generated content by the order of the checkboxes
  const sortAiContentGeneratedRows = (rows) => {
    const contentLabelFields = COMBINED_CONTENT_LABELS.map((item) => item.field);
    return sortBy(rows, (row) => contentLabelFields.indexOf(row.contentType));
  };

  let hasSelectedAiContent = false;
  forEach(COMBINED_CONTENT_LABELS, (item) => {
    if (aiContentData[item.field] === true || aiContentOriginal[item.field] === true) {
      hasSelectedAiContent = true;
      return false;
    }
  });

  const ableToGenerateContent = aiContentEnabled && !isNil(episodeMinutes);

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : ableToGenerateContent ? (
        <Fragment>
          <Label className="tw-cursor-pointer tw-my-4 tw-text-sm">Generate the Following:</Label>
          <Row>
            <Col md={4}>
              <div className="tw-flex tw-flex-col tw-gap-4 tw-grow-0 tw-items-start tw-shrink-0">
                {AiContentData1.map((item, index) => {
                  const aiCheckbox = (
                    <Checkbox
                      checked={aiContentData[item.field] === true}
                      disabled={aiContentOriginal[item.field] === true}
                      key={index}
                      label={item.label}
                      labelClassName="tw-text-sm"
                      name={item.value}
                      onChange={() => onClickAiContent(item)}
                    />
                  );
                  const summaryTranscriptLabel =
                    "Generated automatically with any other content. Will be shown below.";
                  return ["aiTranscript", "aiSummary"].includes(item.field) ? (
                    <UITooltip
                      bgColor="#2E20B1"
                      key={index}
                      label={summaryTranscriptLabel}
                      placement="top"
                    >
                      <div>{aiCheckbox}</div>
                    </UITooltip>
                  ) : (
                    aiCheckbox
                  );
                })}
              </div>
            </Col>
            <Col md={8}>
              {AiContentData2.map((item, index) => (
                <Checkbox
                  checked={aiContentData[item.field] === true}
                  className="tw-mb-4"
                  disabled={aiContentOriginal[item.field] === true}
                  key={index}
                  label={item.label}
                  labelClassName="tw-text-sm"
                  name={item.value}
                  onChange={() => onClickAiContent(item)}
                />
              ))}
            </Col>
            <div className="mt-2 mb-3">
              <CustomButton
                className="tw-mx-4 tw-text-center custom-btn"
                disabled={!hasSelectedAiContent}
                onClick={(e) => {
                  e.preventDefault();
                  if (!aiContentOriginal.aiContentId) {
                    setIsShowModal(true);
                  } else {
                    submitAIContent();
                  }
                }}
              >
                Generate
              </CustomButton>
            </div>
            {isShowModal && (
              <AIContentModal
                confirmFunction={submitAIContent}
                episodeMinutes={episodeMinutes}
                isShowModal={isShowModal}
                minutesBalance={minutesBalance}
                onClickAddMinutes={onClickAddMinutes}
                setIsShowModal={setIsShowModal}
              />
            )}
          </Row>

          {sortAiContentGeneratedRows(aiContentGenerated).map((data, i) => (
            <Fragment key={data.aiContentGeneratedId}>
              <AIContentGeneratedRow
                orderContent={() => confirmOrRegenerateContent(data)}
                key={`row-${data.aiContentGeneratedId}-${i}`}
                data={data}
              />
              {isShowRegenerateModal && data.contentType === "aiTranscript" && (
                <AIContentModal
                  confirmFunction={() => regenerateContent(data)}
                  episodeMinutes={episodeMinutes}
                  isShowModal={isShowRegenerateModal}
                  key={`modal-${data.aiContentGeneratedId}-${i}`}
                  minutesBalance={minutesBalance}
                  onClickAddMinutes={onClickAddMinutes}
                  setIsShowModal={setIsShowRegenerateModal}
                />
              )}
            </Fragment>
          ))}
        </Fragment>
      ) : aiContentEnabled ? (
        <div className="tw-font-Montserrat tw-font-semibold tw-min-h-[150px] tw-mt-4 tw-mb-8 tw-text-black">
          <p>We are currently processing your episode audio. Please check back in a few minutes.</p>
        </div>
      ) : (
        <div className="tw-font-Montserrat tw-font-semibold tw-min-h-[150px] tw-mt-4 tw-mb-8 tw-text-black">
          <p>
            To generate AI Content for your Episode, please{" "}
            <a
              className="tw-text-indigo-800 tw-no-underline hover:tw-no-underline"
              href="#"
              onClick={onClickSubscription}
            >
              update your subscription.
            </a>
          </p>
        </div>
      )}
    </>
  );
}

export default AIContent;
